import type { ErrorObject, ValidateFunction } from 'ajv';
import { validateConformanceReportSchema, validateConformanceReportV02Schema, validateExtensionRegistrySchema, validateGlyphOutlineProjectionSchema, validateGlyphOutlineRegistrySchema, validateGlyphRasterRegistrySchema, validateGlyphRasterRequestSchema, validateGlyphRasterResultSchema, validateHistorySchema, validateHistorySignatureSchema, validateLegacySceneSchema, validatePackageSchema, validatePatchApplicationSchema, validatePatchRejectionSchema, validatePatchSchema, validatePixelMapInspectionSchema, validateReadinessReportSchema, validateRendererRequestSchema, validateRendererRequestV02Schema, validateRendererResponseSchema, validateRendererResponseV02Schema, validateSceneSchema, validateTextShapingPlanSchema, validateTextShapingRegistrySchema, validateVisualConformanceRegistryV02Schema } from './generated/validators';
import type {
  MaipoConformanceReportV01,
  MaipoConformanceReportV02,
  MaipoExtensionRegistryV01,
  MaipoGlyphOutlineBoundsV01,
  MaipoGlyphOutlineProjectionV01,
  MaipoGlyphOutlineRegistryV01,
  MaipoGlyphRasterRequestV01,
  MaipoGlyphRasterRegistryV01,
  MaipoGlyphRasterResultV01,
  MaipoHistorySignatureV01,
  MaipoHistoryV01,
  MaipoPatchV01,
  MaipoPatchApplicationV01,
  MaipoPatchRejectionV01,
  MaipoPixelMapInspectionV01,
  MaipoPackageManifestV01,
  MaipoPublicSceneV00,
  MaipoPublicSceneV01,
  MaipoReadinessReportV01,
  MaipoRendererRequestV01,
  MaipoRendererRequestV02,
  MaipoRendererResponseV01,
  MaipoRendererResponseV02,
  MaipoTextShapingPlanV01,
  MaipoTextShapingRegistryV01,
  MaipoVisualConformanceRegistryV02,
  PublicFormatDiagnostic,
  PublicNode,
  PublicPathObject,
  PublicVectorObject,
} from './types';

const validateLegacyScene = validateLegacySceneSchema as ValidateFunction<MaipoPublicSceneV00>;
const validateScene = validateSceneSchema as ValidateFunction<MaipoPublicSceneV01>;
const validatePatch = validatePatchSchema as ValidateFunction<MaipoPatchV01>;
const validatePackage = validatePackageSchema as ValidateFunction<MaipoPackageManifestV01>;
const validateRendererRequest = validateRendererRequestSchema as ValidateFunction<MaipoRendererRequestV01>;
const validateRendererResponse = validateRendererResponseSchema as ValidateFunction<MaipoRendererResponseV01>;
const validateRendererRequestV02 = validateRendererRequestV02Schema as ValidateFunction<MaipoRendererRequestV02>;
const validateRendererResponseV02 = validateRendererResponseV02Schema as ValidateFunction<MaipoRendererResponseV02>;
const validateConformanceReport = validateConformanceReportSchema as ValidateFunction<MaipoConformanceReportV01>;
const validateConformanceReportV02 = validateConformanceReportV02Schema as ValidateFunction<MaipoConformanceReportV02>;
const validateVisualConformanceRegistryV02 = validateVisualConformanceRegistryV02Schema as ValidateFunction<MaipoVisualConformanceRegistryV02>;
const validateTextShapingPlan = validateTextShapingPlanSchema as ValidateFunction<MaipoTextShapingPlanV01>;
const validateTextShapingRegistry = validateTextShapingRegistrySchema as ValidateFunction<MaipoTextShapingRegistryV01>;
const validateGlyphOutlineProjection = validateGlyphOutlineProjectionSchema as ValidateFunction<MaipoGlyphOutlineProjectionV01>;
const validateGlyphOutlineRegistry = validateGlyphOutlineRegistrySchema as ValidateFunction<MaipoGlyphOutlineRegistryV01>;
const validateGlyphRasterRequest = validateGlyphRasterRequestSchema as ValidateFunction<MaipoGlyphRasterRequestV01>;
const validateGlyphRasterResult = validateGlyphRasterResultSchema as ValidateFunction<MaipoGlyphRasterResultV01>;
const validateGlyphRasterRegistry = validateGlyphRasterRegistrySchema as ValidateFunction<MaipoGlyphRasterRegistryV01>;
const validatePixelMapInspection = validatePixelMapInspectionSchema as ValidateFunction<MaipoPixelMapInspectionV01>;
const validatePatchApplication = validatePatchApplicationSchema as ValidateFunction<MaipoPatchApplicationV01>;
const validatePatchRejection = validatePatchRejectionSchema as ValidateFunction<MaipoPatchRejectionV01>;
const validateHistory = validateHistorySchema as ValidateFunction<MaipoHistoryV01>;
const validateHistorySignature = validateHistorySignatureSchema as ValidateFunction<MaipoHistorySignatureV01>;
const validateExtensionRegistry = validateExtensionRegistrySchema as ValidateFunction<MaipoExtensionRegistryV01>;
const validateReadinessReport = validateReadinessReportSchema as ValidateFunction<MaipoReadinessReportV01>;
const RESERVED_ADAPTER_EXTENSIONS = new Set(['maipo:public-document', 'maipo:public-extensions', 'maipo:public-font-ref', 'maipo:public-role']);

function schemaError(label: string, errors: ErrorObject[] | null | undefined): Error {
  const detail = (errors ?? []).slice(0, 8).map((entry) => {
    const at = entry.instancePath || '/';
    const subject = entry.keyword === 'additionalProperties' && typeof entry.params.additionalProperty === 'string'
      ? ` (${entry.params.additionalProperty})`
      : '';
    return `${at} ${entry.message ?? 'is invalid'}${subject}`;
  }).join('; ');
  return new Error(`${label} is not valid${detail ? `: ${detail}` : '.'}`);
}

export function parseMaipoPublicScene(value: unknown): MaipoPublicSceneV01 {
  if (!validateScene(value)) throw schemaError('Maipo scene', validateScene.errors);
  const semantic = publicSceneDiagnostics(value).filter((entry) => entry.severity === 'error');
  if (semantic.length > 0) throw new Error(`Maipo scene is not valid: ${semantic.map((entry) => entry.message).join('; ')}`);
  return value;
}

export function parseMaipoPublicSceneV00(value: unknown): MaipoPublicSceneV00 {
  if (!validateLegacyScene(value)) throw schemaError('Maipo legacy scene 0.0', validateLegacyScene.errors);
  return value;
}

export function parseMaipoPatch(value: unknown): MaipoPatchV01 {
  if (!validatePatch(value)) throw schemaError('Maipo patch', validatePatch.errors);
  return value;
}

export function parseMaipoPackageManifest(value: unknown): MaipoPackageManifestV01 {
  if (!validatePackage(value)) throw schemaError('Maipo package manifest', validatePackage.errors);
  const ids = new Set<string>();
  for (const asset of value.assets) {
    if (ids.has(asset.id)) throw new Error(`Maipo package manifest repeats asset ID ${asset.id}.`);
    ids.add(asset.id);
    if (asset.path !== `assets/sha256/${asset.digest.slice('sha256:'.length)}`) throw new Error(`Maipo package asset path is not canonical for ${asset.id}.`);
  }
  return value;
}

export function parseMaipoRendererRequest(value: unknown): MaipoRendererRequestV01 {
  if (!validateRendererRequest(value)) throw schemaError('Maipo renderer request', validateRendererRequest.errors);
  if (value.operation === 'render') {
    const scene = parseMaipoPublicScene(value.scene);
    if (value.options.width !== undefined && value.options.width * scene.canvas.height !== value.options.height * scene.canvas.width) {
      throw new Error('Maipo renderer output dimensions must preserve the scene aspect ratio.');
    }
  }
  return value;
}

export function parseMaipoRendererRequestV02(value: unknown): MaipoRendererRequestV02 {
  if (!validateRendererRequestV02(value)) throw schemaError('Maipo renderer request 0.2', validateRendererRequestV02.errors);
  if (value.operation === 'render') {
    const scene = parseMaipoPublicScene(value.scene);
    if (value.options.width !== undefined && value.options.width * scene.canvas.height !== value.options.height * scene.canvas.width) {
      throw new Error('Maipo renderer output dimensions must preserve the scene aspect ratio.');
    }
  }
  return value;
}

export function parseMaipoRendererResponse(value: unknown): MaipoRendererResponseV01 {
  if (!validateRendererResponse(value)) throw schemaError('Maipo renderer response', validateRendererResponse.errors);
  if (value.status === 'ok' && value.operation === 'capabilities') {
    if (new Set(value.capabilities.profiles).size !== value.capabilities.profiles.length) throw new Error('Maipo renderer capabilities repeat a profile.');
    if (new Set(value.capabilities.outputMediaTypes).size !== value.capabilities.outputMediaTypes.length) throw new Error('Maipo renderer capabilities repeat an output media type.');
  }
  if (value.status === 'ok' && value.operation === 'render') {
    const nodeIds = value.nodes?.map((node) => node.id) ?? [];
    if (new Set(nodeIds).size !== nodeIds.length) throw new Error('Maipo renderer response repeats a node ID.');
    const mapIds = value.nodeMap?.ids ?? [];
    if (new Set(mapIds).size !== mapIds.length) throw new Error('Maipo renderer node map repeats an ID.');
  }
  return value;
}

export function parseMaipoRendererResponseV02(value: unknown): MaipoRendererResponseV02 {
  if (!validateRendererResponseV02(value)) throw schemaError('Maipo renderer response 0.2', validateRendererResponseV02.errors);
  if (value.status === 'ok' && value.operation === 'capabilities') {
    if (new Set(value.capabilities.profiles).size !== value.capabilities.profiles.length) throw new Error('Maipo renderer capabilities repeat a profile.');
    if (new Set(value.capabilities.outputMediaTypes).size !== value.capabilities.outputMediaTypes.length) throw new Error('Maipo renderer capabilities repeat an output media type.');
  }
  if (value.status === 'ok' && value.operation === 'render') {
    const nodeIds = value.nodes?.map((node) => node.id) ?? [];
    if (new Set(nodeIds).size !== nodeIds.length) throw new Error('Maipo renderer response repeats a node ID.');
    const mapIds = value.nodeMap?.ids ?? [];
    if (new Set(mapIds).size !== mapIds.length) throw new Error('Maipo renderer node map repeats an ID.');
    const fontIds = value.fonts.map((font) => font.id);
    if (new Set(fontIds).size !== fontIds.length) throw new Error('Maipo renderer response repeats a font ID.');
  }
  return value;
}

export function parseMaipoConformanceReport(value: unknown): MaipoConformanceReportV01 {
  if (!validateConformanceReport(value)) throw schemaError('Maipo conformance report', validateConformanceReport.errors);
  if (new Set(value.checks.structuredErrors).size !== value.checks.structuredErrors.length) throw new Error('Maipo conformance report repeats a structured error code.');
  if (value.nodeMap && new Set(value.nodeMap.ids).size !== value.nodeMap.ids.length) throw new Error('Maipo conformance report repeats a node-map ID.');
  return value;
}

export function parseMaipoConformanceReportV02(value: unknown): MaipoConformanceReportV02 {
  if (!validateConformanceReportV02(value)) throw schemaError('Maipo conformance report 0.2', validateConformanceReportV02.errors);
  if (new Set(value.checks.structuredErrors).size !== value.checks.structuredErrors.length) throw new Error('Maipo conformance report 0.2 repeats a structured error code.');
  if (value.nodeMap && new Set(value.nodeMap.ids).size !== value.nodeMap.ids.length) throw new Error('Maipo conformance report 0.2 repeats a node-map ID.');
  const { metrics, thresholds, reference, actual } = value.visual;
  if (reference.width !== actual.width || reference.height !== actual.height) throw new Error('Maipo conformance report 0.2 compares rasters with different dimensions.');
  if ((value.visual.source === 'renderer-artifact') !== (value.visual.rasterizer === null)) throw new Error('Maipo conformance report 0.2 rasterizer provenance contradicts its visual source.');
  if (value.visual.source === 'renderer-artifact' && (
    value.artifact.mediaType !== 'image/png'
    || value.artifact.width !== actual.width
    || value.artifact.height !== actual.height
    || value.artifact.byteLength !== actual.byteLength
    || value.artifact.digest !== actual.pngDigest
  )) throw new Error('Maipo conformance report 0.2 renderer-artifact evidence differs from its renderer artifact.');
  const pixelCount = reference.width * reference.height;
  const counts = [metrics.differentPixels, metrics.pixelsAboveOne, metrics.pixelsAboveFour, metrics.pixelsAboveSixteen];
  for (const count of counts) {
    if (count > pixelCount) throw new Error('Maipo conformance report 0.2 contains a pixel count larger than its raster.');
  }
  if (
    metrics.pixelsAboveOne > metrics.differentPixels
    || metrics.pixelsAboveFour > metrics.pixelsAboveOne
    || metrics.pixelsAboveSixteen > metrics.pixelsAboveFour
  ) throw new Error('Maipo conformance report 0.2 pixel-difference counts are not monotonic.');
  if (
    metrics.differentPixelRatio !== metrics.differentPixels / pixelCount
    || metrics.pixelsAboveOneRatio !== metrics.pixelsAboveOne / pixelCount
    || metrics.pixelsAboveFourRatio !== metrics.pixelsAboveFour / pixelCount
    || metrics.pixelsAboveSixteenRatio !== metrics.pixelsAboveSixteen / pixelCount
  ) throw new Error('Maipo conformance report 0.2 pixel ratios differ from their counts.');
  if (
    metrics.differentPixelRatio > thresholds.maxDifferentPixelRatio
    || metrics.pixelsAboveFourRatio > thresholds.maxPixelsAboveFourRatio
    || metrics.meanChannelDelta > thresholds.maxMeanChannelDelta
    || metrics.maxChannelDelta > thresholds.maxChannelDelta
  ) throw new Error('Maipo conformance report 0.2 claims success outside its visual thresholds.');
  return value;
}

export function parseMaipoVisualConformanceRegistryV02(value: unknown): MaipoVisualConformanceRegistryV02 {
  if (!validateVisualConformanceRegistryV02(value)) throw schemaError('Maipo visual conformance registry 0.2', validateVisualConformanceRegistryV02.errors);
  const ids = value.cases.map(({ id }) => id);
  if (new Set(ids).size !== ids.length) throw new Error('Maipo visual conformance registry 0.2 repeats a case ID.');
  return value;
}

const GLYPH_FLAG_ORDER = ['unsafe-to-break', 'unsafe-to-concat', 'safe-to-insert-tatweel'] as const;

function unicodeScalarLength(text: string): number {
  let length = 0;
  for (let index = 0; index < text.length; index += 1) {
    const unit = text.charCodeAt(index);
    if (unit >= 0xd800 && unit <= 0xdbff) {
      const next = text.charCodeAt(index + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) throw new Error('Maipo text shaping plan source contains an unpaired UTF-16 surrogate.');
      index += 1;
    } else if (unit >= 0xdc00 && unit <= 0xdfff) throw new Error('Maipo text shaping plan source contains an unpaired UTF-16 surrogate.');
    length += 1;
  }
  return length;
}

/** Validate schema plus the cross-field invariants that JSON Schema cannot express. */
export function parseMaipoTextShapingPlan(value: unknown): MaipoTextShapingPlanV01 {
  if (!validateTextShapingPlan(value)) throw schemaError('Maipo text shaping plan', validateTextShapingPlan.errors);
  const scalarLength = unicodeScalarLength(value.source.text);
  if (scalarLength === 0 && value.runs.length !== 0) throw new Error('Maipo text shaping plan for empty source text must contain no runs.');
  if (scalarLength > 0 && value.runs.length === 0) throw new Error('Maipo text shaping plan runs do not cover its source text.');
  const runIds = new Set<string>();
  const ranges = value.runs.map((run) => {
    if (runIds.has(run.id)) throw new Error(`Maipo text shaping plan repeats run ID ${run.id}.`);
    runIds.add(run.id);
    if (run.sourceStart >= run.sourceEnd || run.sourceEnd > scalarLength) throw new Error(`Maipo text shaping run ${run.id} has an invalid source range.`);
    const featureTags = run.features.map(({ tag }) => tag);
    if (new Set(featureTags).size !== featureTags.length) throw new Error(`Maipo text shaping run ${run.id} repeats an OpenType feature tag.`);
    let previousCluster: number | undefined;
    for (const glyph of run.glyphs) {
      if (glyph.cluster < run.sourceStart || glyph.cluster >= run.sourceEnd) throw new Error(`Maipo text shaping run ${run.id} contains a glyph cluster outside its source range.`);
      if (previousCluster !== undefined) {
        const monotone = run.direction === 'ltr' || run.direction === 'ttb'
          ? glyph.cluster >= previousCluster
          : glyph.cluster <= previousCluster;
        if (!monotone) throw new Error(`Maipo text shaping run ${run.id} has non-monotone glyph clusters.`);
      }
      previousCluster = glyph.cluster;
      const flags = glyph.flags ?? [];
      if (new Set(flags).size !== flags.length) throw new Error(`Maipo text shaping run ${run.id} repeats a glyph flag.`);
      for (let index = 1; index < flags.length; index += 1) {
        if (GLYPH_FLAG_ORDER.indexOf(flags[index]!) <= GLYPH_FLAG_ORDER.indexOf(flags[index - 1]!)) throw new Error(`Maipo text shaping run ${run.id} glyph flags are not in canonical order.`);
      }
    }
    return { start: run.sourceStart, end: run.sourceEnd, id: run.id };
  }).sort((left, right) => left.start - right.start || left.end - right.end);
  let cursor = 0;
  for (const range of ranges) {
    if (range.start !== cursor) throw new Error(`Maipo text shaping runs overlap or leave a source gap before ${range.id}.`);
    cursor = range.end;
  }
  if (cursor !== scalarLength) throw new Error('Maipo text shaping plan runs do not cover its source text.');
  const variationTags = value.font.variations.map(({ tag }) => tag);
  if (new Set(variationTags).size !== variationTags.length) throw new Error('Maipo text shaping plan repeats a font variation tag.');
  return value;
}

/** Verify content identities after synchronous structural and semantic validation. */
export async function verifyMaipoTextShapingPlan(
  value: unknown,
  options: { fontBytes?: Uint8Array } = {},
): Promise<MaipoTextShapingPlanV01> {
  const plan = parseMaipoTextShapingPlan(value);
  const textBytes = new TextEncoder().encode(plan.source.text);
  if (await bytesDigest(textBytes) !== plan.source.textDigest) throw new Error('Maipo text shaping plan source text does not match its SHA-256 digest.');
  if (options.fontBytes && await bytesDigest(options.fontBytes) !== plan.font.digest) throw new Error('Maipo text shaping plan font bytes do not match its SHA-256 digest.');
  return plan;
}

export function parseMaipoTextShapingRegistry(value: unknown): MaipoTextShapingRegistryV01 {
  if (!validateTextShapingRegistry(value)) throw schemaError('Maipo text shaping registry', validateTextShapingRegistry.errors);
  const ids = value.cases.map(({ id }) => id);
  if (new Set(ids).size !== ids.length) throw new Error('Maipo text shaping registry repeats a case ID.');
  if (new Set(value.scope.verified).size !== value.scope.verified.length || new Set(value.scope.notYetDefined).size !== value.scope.notYetDefined.length) {
    throw new Error('Maipo text shaping registry repeats a scope claim.');
  }
  const verified = new Set(value.scope.verified);
  if (value.scope.notYetDefined.some((claim) => verified.has(claim))) throw new Error('Maipo text shaping registry contradicts its scope claims.');
  return value;
}

function equalOutlineBounds(left: MaipoGlyphOutlineBoundsV01 | null, right: MaipoGlyphOutlineBoundsV01 | null): boolean {
  return left === null || right === null
    ? left === right
    : left.xMin === right.xMin && left.yMin === right.yMin && left.xMax === right.xMax && left.yMax === right.yMax;
}

function unionOutlineBounds(
  left: MaipoGlyphOutlineBoundsV01 | null,
  right: MaipoGlyphOutlineBoundsV01,
): MaipoGlyphOutlineBoundsV01 {
  return left === null ? { ...right } : {
    xMin: Math.min(left.xMin, right.xMin),
    yMin: Math.min(left.yMin, right.yMin),
    xMax: Math.max(left.xMax, right.xMax),
    yMax: Math.max(left.yMax, right.yMax),
  };
}

function validateOutlinePath(runId: string, path: MaipoGlyphOutlineProjectionV01['runs'][number]['glyphs'][number]['path']): void {
  let open = false;
  let draws = false;
  for (const command of path) {
    if (command.op === 'M') {
      if (open) throw new Error(`Maipo glyph outline projection ${runId} starts a new contour before closing the previous contour.`);
      open = true;
      draws = false;
    } else if (command.op === 'Z') {
      if (!open || !draws) throw new Error(`Maipo glyph outline projection ${runId} closes a missing or empty contour.`);
      open = false;
    } else {
      if (!open) throw new Error(`Maipo glyph outline projection ${runId} draws outside a contour.`);
      draws = true;
    }
  }
  if (open) throw new Error(`Maipo glyph outline projection ${runId} leaves an outline contour open.`);
}

export function parseMaipoGlyphOutlineProjection(value: unknown): MaipoGlyphOutlineProjectionV01 {
  if (!validateGlyphOutlineProjection(value)) throw schemaError('Maipo glyph outline projection', validateGlyphOutlineProjection.errors);
  const runIds = value.runs.map(({ runId }) => runId);
  if (new Set(runIds).size !== runIds.length) throw new Error('Maipo glyph outline projection repeats a run ID.');
  let union: MaipoGlyphOutlineBoundsV01 | null = null;
  for (const run of value.runs) for (const glyph of run.glyphs) {
    validateOutlinePath(run.runId, glyph.path);
    if (glyph.bounds) {
      if (glyph.bounds.xMin > glyph.bounds.xMax || glyph.bounds.yMin > glyph.bounds.yMax) throw new Error(`Maipo glyph outline projection ${run.runId} has inverted glyph bounds.`);
      union = unionOutlineBounds(union, glyph.bounds);
    }
    if ((glyph.path.length === 0) !== (glyph.bounds === null)) throw new Error(`Maipo glyph outline projection ${run.runId} path and bounds disagree.`);
  }
  if (!equalOutlineBounds(value.bounds, union)) throw new Error('Maipo glyph outline projection aggregate bounds do not equal the union of its glyph bounds.');
  return value;
}

export function parseMaipoGlyphOutlineRegistry(value: unknown): MaipoGlyphOutlineRegistryV01 {
  if (!validateGlyphOutlineRegistry(value)) throw schemaError('Maipo glyph outline registry', validateGlyphOutlineRegistry.errors);
  const ids = value.cases.map(({ id }) => id);
  if (new Set(ids).size !== ids.length) throw new Error('Maipo glyph outline registry repeats a case ID.');
  const resources = value.cases.flatMap(({ plan, projection, svg }) => [plan, projection, svg]);
  if (new Set(resources).size !== resources.length) throw new Error('Maipo glyph outline registry repeats a fixture resource.');
  if (new Set(value.scope.verified).size !== value.scope.verified.length || new Set(value.scope.notYetDefined).size !== value.scope.notYetDefined.length) {
    throw new Error('Maipo glyph outline registry repeats a scope claim.');
  }
  const verified = new Set(value.scope.verified);
  if (value.scope.notYetDefined.some((claim) => verified.has(claim))) throw new Error('Maipo glyph outline registry contradicts its scope claims.');
  return value;
}

export function parseMaipoGlyphRasterRequest(value: unknown): MaipoGlyphRasterRequestV01 {
  if (!validateGlyphRasterRequest(value)) throw schemaError('Maipo glyph raster request', validateGlyphRasterRequest.errors);
  parseMaipoGlyphOutlineProjection(value.projection);
  const pixels = value.options.width * value.options.height;
  if (!Number.isSafeInteger(pixels) || pixels > 4_194_304) throw new Error('Maipo glyph raster request exceeds the 4,194,304-pixel limit.');
  if (value.options.padding * 2 >= value.options.width || value.options.padding * 2 >= value.options.height) {
    throw new Error('Maipo glyph raster request padding leaves no drawable viewport.');
  }
  return value;
}

export function parseMaipoGlyphRasterResult(value: unknown): MaipoGlyphRasterResultV01 {
  if (!validateGlyphRasterResult(value)) throw schemaError('Maipo glyph raster result', validateGlyphRasterResult.errors);
  if (value.artifact.width !== value.options.width || value.artifact.height !== value.options.height) {
    throw new Error('Maipo glyph raster result dimensions differ from its options.');
  }
  const glyphIds = value.glyphs.map(({ id }) => id);
  if (new Set(glyphIds).size !== glyphIds.length) throw new Error('Maipo glyph raster result repeats a glyph metadata ID.');
  for (const glyph of value.glyphs) {
    if (glyph.id !== `${glyph.runId}/glyph/${String(glyph.glyphIndex)}`) throw new Error(`Maipo glyph raster result has a noncanonical glyph metadata ID ${glyph.id}.`);
    if ((glyph.bounds === null) !== !glyph.visible) throw new Error(`Maipo glyph raster result visibility and bounds disagree for ${glyph.id}.`);
    if (glyph.bounds && (glyph.bounds.x + glyph.bounds.width > value.artifact.width || glyph.bounds.y + glyph.bounds.height > value.artifact.height)) {
      throw new Error(`Maipo glyph raster result bounds exceed the artifact for ${glyph.id}.`);
    }
  }
  if (value.options.includeGlyphMap !== (value.glyphMap !== null)) throw new Error('Maipo glyph raster result glyph-map presence differs from its request options.');
  if (value.glyphMap) {
    if (value.glyphMap.width !== value.artifact.width || value.glyphMap.height !== value.artifact.height) throw new Error('Maipo glyph raster result glyph-map dimensions differ from its artifact.');
    if (new Set(value.glyphMap.ids).size !== value.glyphMap.ids.length) throw new Error('Maipo glyph raster result repeats a glyph-map ID.');
    if (value.glyphMap.ids.length !== glyphIds.length || value.glyphMap.ids.some((id, index) => id !== glyphIds[index])) {
      throw new Error('Maipo glyph raster result glyph-map IDs differ from its ordered glyph metadata.');
    }
  }
  const phases = [value.timing.validationMs, value.timing.renderMs, value.timing.glyphMapMs, value.timing.encodeMs];
  if (phases.some((phase) => phase > value.timing.totalMs)) throw new Error('Maipo glyph raster result phase timing exceeds its total timing.');
  return value;
}

export function parseMaipoGlyphRasterRegistry(value: unknown): MaipoGlyphRasterRegistryV01 {
  if (!validateGlyphRasterRegistry(value)) throw schemaError('Maipo glyph raster registry', validateGlyphRasterRegistry.errors);
  const caseIds = value.cases.map(({ id }) => id);
  if (new Set(caseIds).size !== caseIds.length) throw new Error('Maipo glyph raster registry repeats a case ID.');
  const resources = value.cases.flatMap(({ projection, request, png, glyphMap }) => [projection, request, png, glyphMap]);
  if (new Set(resources).size !== resources.length) throw new Error('Maipo glyph raster registry repeats a fixture resource.');
  for (const entry of value.cases) {
    const glyphIds = entry.glyphs.map(({ id }) => id);
    if (new Set(glyphIds).size !== glyphIds.length) throw new Error(`Maipo glyph raster registry repeats a glyph ID in ${entry.id}.`);
    if (entry.assertions.glyphCount !== entry.glyphs.length) throw new Error(`Maipo glyph raster registry glyph count disagrees for ${entry.id}.`);
    if (entry.assertions.outlineGlyphCount !== entry.glyphs.filter(({ visible }) => visible).length) throw new Error(`Maipo glyph raster registry outline glyph count disagrees for ${entry.id}.`);
  }
  if (new Set(value.scope.verified).size !== value.scope.verified.length || new Set(value.scope.notYetDefined).size !== value.scope.notYetDefined.length) {
    throw new Error('Maipo glyph raster registry repeats a scope claim.');
  }
  const verified = new Set(value.scope.verified);
  if (value.scope.notYetDefined.some((claim) => verified.has(claim))) throw new Error('Maipo glyph raster registry contradicts its scope claims.');
  return value;
}

export function parseMaipoPixelMapInspection(value: unknown): MaipoPixelMapInspectionV01 {
  if (!validatePixelMapInspection(value)) throw schemaError('Maipo pixel-map inspection', validatePixelMapInspection.errors);
  const { source, region } = value;
  if (source.width * source.height > 4_194_304) throw new Error('Maipo pixel-map inspection source exceeds the 4,194,304-pixel SDK limit.');
  if (region.x + region.width > source.width || region.y + region.height > source.height) throw new Error('Maipo pixel-map inspection region exceeds its source bounds.');
  if (value.pixelCount !== region.width * region.height) throw new Error('Maipo pixel-map inspection pixel count differs from its region.');
  if (new Set(source.ids).size !== source.ids.length) throw new Error('Maipo pixel-map inspection repeats a source owner ID.');
  const owners = new Set<number>();
  let accountedPixels = value.unownedPixels;
  for (let index = 0; index < value.owners.length; index += 1) {
    const owner = value.owners[index]!;
    if (owners.has(owner.owner)) throw new Error('Maipo pixel-map inspection repeats an owner index.');
    owners.add(owner.owner);
    if (owner.owner > source.ids.length || owner.id !== source.ids[owner.owner - 1]) throw new Error(`Maipo pixel-map inspection owner ${String(owner.owner)} does not match its source ID table.`);
    if (owner.coverage !== owner.pixels / value.pixelCount) throw new Error(`Maipo pixel-map inspection coverage disagrees for ${owner.id}.`);
    if (owner.bounds.x < region.x || owner.bounds.y < region.y || owner.bounds.x + owner.bounds.width > region.x + region.width || owner.bounds.y + owner.bounds.height > region.y + region.height) {
      throw new Error(`Maipo pixel-map inspection bounds exceed the query region for ${owner.id}.`);
    }
    const previous = value.owners[index - 1];
    if (previous && (previous.pixels < owner.pixels || (previous.pixels === owner.pixels && previous.owner > owner.owner))) throw new Error('Maipo pixel-map inspection owners are not in deterministic coverage order.');
    accountedPixels += owner.pixels;
  }
  if (accountedPixels !== value.pixelCount) throw new Error('Maipo pixel-map inspection owner counts do not account for its region.');
  return value;
}

export function parseMaipoPatchApplication(value: unknown): MaipoPatchApplicationV01 {
  if (!validatePatchApplication(value)) throw schemaError('Maipo patch application', validatePatchApplication.errors);
  if (value.resultRevision !== value.baseRevision + 1) throw new Error('Maipo patch application result revision must immediately follow its base revision.');
  return value;
}

export function parseMaipoPatchRejection(value: unknown): MaipoPatchRejectionV01 {
  if (!validatePatchRejection(value)) throw schemaError('Maipo patch rejection', validatePatchRejection.errors);
  const documentMismatch = value.documentId !== value.sourceDocumentId;
  const revisionMismatch = value.baseRevision !== value.sourceRevision;
  if ((value.reason === 'document-mismatch') !== documentMismatch) {
    throw new Error('Maipo patch rejection document mismatch reason disagrees with its document IDs.');
  }
  if (value.reason !== 'document-mismatch' && (value.reason === 'stale-revision') !== revisionMismatch) {
    throw new Error('Maipo patch rejection stale revision reason disagrees with its revisions.');
  }
  if (value.operationIndex !== undefined && value.operationIndex >= value.operationCount) {
    throw new Error('Maipo patch rejection operation index exceeds its patch operation count.');
  }
  return value;
}

export function parseMaipoHistory(value: unknown): MaipoHistoryV01 {
  if (!validateHistory(value)) throw schemaError('Maipo history', validateHistory.errors);
  let expectedRevision = value.genesis.revision;
  let expectedSceneDigest = value.genesis.sceneDigest;
  let previousEntryDigest: string | null = null;
  const patchIds = new Set<string>();
  for (let index = 0; index < value.entries.length; index += 1) {
    const entry = value.entries[index]!;
    const application = parseMaipoPatchApplication(entry.application);
    if (entry.sequence !== index + 1) throw new Error('Maipo history entry sequence is not contiguous and one-based.');
    if (entry.previousEntryDigest !== previousEntryDigest) throw new Error(`Maipo history entry ${String(entry.sequence)} does not reference its immediate predecessor.`);
    if (application.documentId !== value.documentId) throw new Error(`Maipo history entry ${String(entry.sequence)} targets a different document.`);
    if (application.baseRevision !== expectedRevision || application.sourceSceneDigest !== expectedSceneDigest) {
      throw new Error(`Maipo history entry ${String(entry.sequence)} does not continue the previous scene revision and digest.`);
    }
    if (patchIds.has(application.clientPatchId)) throw new Error(`Maipo history repeats client patch ID ${application.clientPatchId}.`);
    patchIds.add(application.clientPatchId);
    expectedRevision = application.resultRevision;
    expectedSceneDigest = application.resultSceneDigest;
    previousEntryDigest = entry.entryDigest;
  }
  return value;
}

export function parseMaipoHistorySignature(value: unknown): MaipoHistorySignatureV01 {
  if (!validateHistorySignature(value)) throw schemaError('Maipo history signature', validateHistorySignature.errors);
  return value;
}

export function parseMaipoExtensionRegistry(value: unknown): MaipoExtensionRegistryV01 {
  if (!validateExtensionRegistry(value)) throw schemaError('Maipo extension registry', validateExtensionRegistry.errors);
  const reservedKeys = value.reserved.map(({ key }) => key);
  if (new Set(reservedKeys).size !== reservedKeys.length) throw new Error('Maipo extension registry repeats a reserved key.');
  const entryIdentities = value.entries.map(({ key, version }) => `${key}\u0000${version}`);
  if (new Set(entryIdentities).size !== entryIdentities.length) throw new Error('Maipo extension registry repeats an entry key and version.');
  const reserved = new Set(reservedKeys);
  if (value.entries.some(({ key }) => reserved.has(key))) throw new Error('Maipo extension registry registers an adapter-reserved key.');
  return value;
}

export function parseMaipoReadinessReport(value: unknown): MaipoReadinessReportV01 {
  if (!validateReadinessReport(value)) throw schemaError('Maipo readiness report', validateReadinessReport.errors);
  const ids = value.requirements.map(({ id }) => id);
  if (new Set(ids).size !== ids.length) throw new Error('Maipo readiness report repeats a requirement ID.');
  const actual = { achieved: 0, partial: 0, missing: 0 };
  for (const requirement of value.requirements) {
    actual[requirement.status] += 1;
    if (requirement.status === 'achieved' && (requirement.gaps.length !== 0 || requirement.exitCriteria.length !== 0)) {
      throw new Error(`Achieved Maipo readiness requirement ${requirement.id} still declares a gap or exit criterion.`);
    }
    if (requirement.status !== 'achieved' && (requirement.gaps.length === 0 || requirement.exitCriteria.length === 0)) {
      throw new Error(`Incomplete Maipo readiness requirement ${requirement.id} must declare gaps and exit criteria.`);
    }
  }
  if (value.summary.total !== value.requirements.length
    || value.summary.achieved !== actual.achieved
    || value.summary.partial !== actual.partial
    || value.summary.missing !== actual.missing) {
    throw new Error('Maipo readiness summary does not match its requirements.');
  }
  if ((value.status === 'not-ready') !== (actual.partial > 0 || actual.missing > 0)) {
    throw new Error('Maipo readiness status contradicts its incomplete requirements.');
  }
  return value;
}

function pathDiagnostics(object: PublicPathObject): PublicFormatDiagnostic[] {
  const diagnostics: PublicFormatDiagnostic[] = [];
  const nodes = new Map(object.geometry.nodes.map((node) => [node.id, node]));
  const subpaths = new Set(object.geometry.subpaths.map((subpath) => subpath.id));
  for (const node of object.geometry.nodes) {
    if (!subpaths.has(node.subpathId)) diagnostics.push({
      severity: 'error',
      code: 'missing-subpath',
      target: node.id,
      message: `Path node ${node.id} references missing subpath ${node.subpathId}.`,
    });
  }
  for (const subpath of object.geometry.subpaths) {
    for (const nodeId of subpath.nodeIds) {
      const node = nodes.get(nodeId);
      if (!node) diagnostics.push({
        severity: 'error',
        code: 'missing-path-node',
        target: subpath.id,
        message: `Subpath ${subpath.id} references missing node ${nodeId}.`,
      });
      else if (node.subpathId !== subpath.id) diagnostics.push({
        severity: 'error',
        code: 'cross-subpath-node',
        target: subpath.id,
        message: `Subpath ${subpath.id} references node ${nodeId} owned by ${node.subpathId}.`,
      });
    }
  }
  return diagnostics;
}

/**
 * What a reader supports. A reader that interprets no extension at all passes
 * nothing, which is the safe default: every required extension is unsupported.
 */
export interface SceneSupportOptions {
  /** Exact extension contracts this reader interprets, not merely preserves. */
  supportedExtensions?: readonly UnsupportedExtension[];
}

export interface UnsupportedExtension {
  key: string;
  version: string;
  schemaId?: string;
}

export interface SceneSupportDisposition {
  status: 'supported' | 'unsupported';
  unsupported: UnsupportedExtension[];
}

/**
 * Whether a reader can honestly present this document.
 *
 * This is deliberately not a validity check. A scene declaring a required
 * extension is well formed, and a reader that cannot interpret it must still be
 * able to parse, canonicalize and round-trip it byte-exactly — preserving a
 * document is how it survives passing through software that does not understand
 * it. What such a reader must not do is present it as if nothing were missing:
 * a still frame rendered from a document whose author required a declarative
 * animation profile misrepresents the document.
 *
 * So support is reported separately from validity, and the two safe responses
 * named in `spec/EXTENSIONS-0.1.md` — preserve, or reject — are both reachable
 * from this one answer. Optional declarations never make a document
 * unsupported; they are preserved and ignored.
 */
export function sceneSupportDisposition(
  scene: MaipoPublicSceneV01,
  options: SceneSupportOptions = {},
): SceneSupportDisposition {
  const supported = options.supportedExtensions ?? [];
  const unsupported: UnsupportedExtension[] = [];
  for (const declaration of scene.extensionDeclarations ?? []) {
    if (declaration.required !== true) continue;
    if (supported.some(entry => entry.key === declaration.key && entry.version === declaration.version && entry.schemaId === declaration.schemaId)) continue;
    unsupported.push({
      key: declaration.key,
      version: declaration.version,
      ...(declaration.schemaId === undefined ? {} : { schemaId: declaration.schemaId }),
    });
  }
  return { status: unsupported.length > 0 ? 'unsupported' : 'supported', unsupported };
}

/** Admission diagnostics for static readers; parsing and preservation stay separate. */
export function sceneSupportDiagnostics(scene: MaipoPublicSceneV01): PublicFormatDiagnostic[] {
  return sceneSupportDisposition(scene).unsupported.map(extension => ({
    severity: 'error',
    code: 'unsupported-required-extension',
    target: extension.key,
    message: `Required extension ${extension.key} version ${extension.version}${extension.schemaId ? ` (${extension.schemaId})` : ''} is not supported by this static reader.`,
  }));
}

export function publicSceneDiagnostics(scene: MaipoPublicSceneV01): PublicFormatDiagnostic[] {
  const diagnostics: PublicFormatDiagnostic[] = [];
  const ids = new Map<string, string>();
  const assets = new Map(scene.assets.map((asset) => [asset.id, asset]));
  const fonts = new Map(scene.fonts.map((font) => [font.id, font]));
  const declarations = new Map<string, number>();
  const usedExtensions = new Map<string, string[]>();

  for (const declaration of scene.extensionDeclarations ?? []) {
    declarations.set(declaration.key, (declarations.get(declaration.key) ?? 0) + 1);
    if (RESERVED_ADAPTER_EXTENSIONS.has(declaration.key)) diagnostics.push({
      severity: 'error',
      code: 'reserved-extension-key',
      target: scene.documentId,
      message: `Extension declaration ${declaration.key} is reserved for Maipo's native adapter metadata.`,
    });
  }
  for (const [key, count] of declarations) if (count > 1) diagnostics.push({
    severity: 'error',
    code: 'duplicate-extension-declaration',
    target: scene.documentId,
    message: `Extension ${key} has ${String(count)} declarations.`,
  });

  const checkExtensions = (target: string, extensions: Record<string, unknown> | undefined) => {
    for (const key of Object.keys(extensions ?? {})) {
      const targets = usedExtensions.get(key) ?? [];
      targets.push(target);
      usedExtensions.set(key, targets);
      if (RESERVED_ADAPTER_EXTENSIONS.has(key)) diagnostics.push({
        severity: 'error',
        code: 'reserved-extension-key',
        target,
        message: `Extension key ${key} is reserved for Maipo's native adapter metadata.`,
      });
    }
  };

  const claim = (id: string, kind: string) => {
    const prior = ids.get(id);
    if (prior) diagnostics.push({
      severity: 'error',
      code: 'duplicate-id',
      target: id,
      message: `ID ${id} is used by both ${prior} and ${kind}.`,
    });
    else ids.set(id, kind);
  };

  claim(scene.documentId, 'document');
  for (const asset of scene.assets) claim(asset.id, 'asset');
  for (const font of scene.fonts) {
    claim(font.id, 'font');
    if (font.source.kind === 'system') diagnostics.push({
      severity: 'warning',
      code: 'non-portable-system-font',
      target: font.id,
      message: `Font ${font.id} depends on the rendering environment.`,
    });
    else {
      const asset = assets.get(font.source.asset);
      if (!asset) diagnostics.push({
        severity: 'error',
        code: 'missing-font-asset',
        target: font.id,
        message: `Font ${font.id} references missing asset ${font.source.asset}.`,
      });
      else if (!asset.mediaType.startsWith('font/')) diagnostics.push({
        severity: 'error',
        code: 'wrong-font-asset-type',
        target: font.id,
        message: `Font ${font.id} references non-font asset ${asset.id}.`,
      });
      if (font.license.embedding === 'restricted') diagnostics.push({
        severity: 'warning',
        code: 'font-embedding-restricted',
        target: font.id,
        message: `Embedded font ${font.id} declares restricted embedding rights; consumers must not redistribute it without separate authorization.`,
      });
      else if (font.license.embedding === 'unknown') diagnostics.push({
        severity: 'warning',
        code: 'font-embedding-unknown',
        target: font.id,
        message: `Embedded font ${font.id} does not declare known embedding rights; portability and redistribution require review.`,
      });
    }
  }

  const visitObject = (object: PublicVectorObject) => {
    claim(object.id, `vector object ${object.type}`);
    checkExtensions(object.id, object.extensions);
    if (object.type === 'path') {
      for (const node of object.geometry.nodes) claim(node.id, 'path node');
      for (const subpath of object.geometry.subpaths) claim(subpath.id, 'path subpath');
      diagnostics.push(...pathDiagnostics(object));
    }
  };

  const visitNode = (node: PublicNode) => {
    claim(node.id, `${node.type} node`);
    checkExtensions(node.id, node.extensions);
    if (node.type === 'group') node.children.forEach(visitNode);
    else if (node.type === 'vector') node.objects.forEach(visitObject);
    else if (node.type === 'image') {
      const asset = assets.get(node.asset);
      if (!asset) diagnostics.push({
        severity: 'error',
        code: 'missing-image-asset',
        target: node.id,
        message: `Image ${node.id} references missing asset ${node.asset}.`,
      });
      else if (!asset.mediaType.startsWith('image/')) diagnostics.push({
        severity: 'error',
        code: 'wrong-image-asset-type',
        target: node.id,
        message: `Image ${node.id} references non-image asset ${asset.id}.`,
      });
    } else if (node.type === 'text') {
      if (!node.style.fontRef) diagnostics.push({
        severity: 'warning',
        code: 'unbound-text-font',
        target: node.id,
        message: `Text ${node.id} has no fontRef; family matching depends on the rendering environment.`,
      });
      else {
        const font = fonts.get(node.style.fontRef);
        if (!font) diagnostics.push({
          severity: 'error',
          code: 'missing-font',
          target: node.id,
          message: `Text ${node.id} references missing font ${node.style.fontRef}.`,
        });
        else if (font.family !== node.style.fontFamily || font.weight !== node.style.fontWeight) diagnostics.push({
          severity: 'error',
          code: 'font-reference-mismatch',
          target: node.id,
          message: `Text ${node.id} does not match the family and weight declared by ${font.id}.`,
        });
      }
    }
  };
  checkExtensions(scene.documentId, scene.extensions);
  scene.children.forEach(visitNode);
  for (const [key, targets] of usedExtensions) if (!declarations.has(key) && !RESERVED_ADAPTER_EXTENSIONS.has(key)) diagnostics.push({
    severity: 'warning',
    code: 'undeclared-extension',
    target: targets[0]!,
    message: `Extension ${key} is used without a public declaration; 0.1 preserves it as opaque metadata, but a future version will require a declaration.`,
  });
  return diagnostics;
}

function decodeBase64(value: string): Uint8Array {
  const binary = atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function hex(bytes: Uint8Array): string {
  return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

function isCanonicalBase64(value: string): boolean {
  return value.length % 4 === 0 && /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(value);
}

async function bytesDigest(bytes: Uint8Array): Promise<string> {
  return `sha256:${hex(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)))}`;
}

function rendererBytes(data: string, label: string): Uint8Array {
  if (!isCanonicalBase64(data)) throw new Error(`${label} is not canonical base64.`);
  try { return decodeBase64(data); }
  catch { throw new Error(`${label} is not valid base64.`); }
}

async function verifyParsedMaipoRendererResponse<T extends MaipoRendererResponseV01 | MaipoRendererResponseV02>(
  response: T,
  requireMappedNodeMetadata: boolean,
): Promise<T> {
  if (response.status !== 'ok' || response.operation !== 'render') return response;
  const artifact = rendererBytes(response.artifact.data, 'Maipo renderer artifact');
  if (artifact.byteLength !== response.artifact.byteLength) throw new Error('Maipo renderer artifact byte length does not match its data.');
  if (await bytesDigest(artifact) !== response.artifact.digest) throw new Error('Maipo renderer artifact does not match its SHA-256 digest.');
  if (response.artifact.mediaType === 'image/png') {
    const signature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
    if (artifact.byteLength < signature.length || !signature.every((byte, index) => artifact[index] === byte)) throw new Error('Maipo renderer PNG artifact has no PNG signature.');
  } else {
    let svg: string;
    try { svg = new TextDecoder('utf-8', { fatal: true }).decode(artifact); }
    catch { throw new Error('Maipo renderer SVG artifact is not UTF-8.'); }
    if (!/^\s*(?:<\?xml[^>]*>\s*)?<svg(?:\s|>)/u.test(svg)) throw new Error('Maipo renderer SVG artifact has no SVG root element.');
  }
  if (response.nodeMap) {
    if (response.nodeMap.width !== response.artifact.width || response.nodeMap.height !== response.artifact.height) throw new Error('Maipo renderer node-map dimensions do not match the artifact.');
    const nodeMap = rendererBytes(response.nodeMap.data, 'Maipo renderer node map');
    const expectedBytes = response.nodeMap.width * response.nodeMap.height * 4;
    if (!Number.isSafeInteger(expectedBytes) || expectedBytes !== response.nodeMap.byteLength || nodeMap.byteLength !== expectedBytes) throw new Error('Maipo renderer node-map dimensions and byte length disagree.');
    if (await bytesDigest(nodeMap) !== response.nodeMap.digest) throw new Error('Maipo renderer node map does not match its SHA-256 digest.');
    const returnedNodeIds = requireMappedNodeMetadata && response.nodes ? new Set(response.nodes.map((node) => node.id)) : null;
    if (returnedNodeIds) for (const id of response.nodeMap.ids) {
      if (!returnedNodeIds.has(id)) throw new Error(`Maipo renderer node map references node ${id}, which is absent from the returned node metadata.`);
    }
    const view = new DataView(nodeMap.buffer, nodeMap.byteOffset, nodeMap.byteLength);
    for (let offset = 0; offset < nodeMap.byteLength; offset += 4) if (view.getUint32(offset, true) > response.nodeMap.ids.length) throw new Error('Maipo renderer node map contains an undeclared pixel owner.');
  }
  return response;
}

export async function verifyMaipoRendererResponse(value: unknown): Promise<MaipoRendererResponseV01> {
  return verifyParsedMaipoRendererResponse(parseMaipoRendererResponse(value), false);
}

export async function verifyMaipoRendererResponseV02(value: unknown): Promise<MaipoRendererResponseV02> {
  return verifyParsedMaipoRendererResponse(parseMaipoRendererResponseV02(value), true);
}

export async function verifyPublicAssets(scene: MaipoPublicSceneV01): Promise<PublicFormatDiagnostic[]> {
  const diagnostics: PublicFormatDiagnostic[] = [];
  for (const asset of scene.assets) {
    let bytes: Uint8Array;
    try {
      bytes = decodeBase64(asset.data);
    } catch {
      diagnostics.push({ severity: 'error', code: 'invalid-base64', target: asset.id, message: `Asset ${asset.id} is not valid base64.` });
      continue;
    }
    if (bytes.byteLength !== asset.byteLength) diagnostics.push({
      severity: 'error',
      code: 'asset-length-mismatch',
      target: asset.id,
      message: `Asset ${asset.id} declares ${String(asset.byteLength)} bytes but contains ${String(bytes.byteLength)}.`,
    });
    const digest = await bytesDigest(bytes);
    if (digest !== asset.digest) diagnostics.push({
      severity: 'error',
      code: 'asset-digest-mismatch',
      target: asset.id,
      message: `Asset ${asset.id} does not match its SHA-256 digest.`,
    });
  }
  return diagnostics;
}
